import { useEffect, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { SocialLink } from './types'

export interface MobileSidebarProps {
  open: boolean
  onClose: () => void
  logo: string
  homeTo: string
  followTitle: string
  socials: SocialLink[]
  /**
   * The other KKF apps, by absolute URL — the Foundation, the resort, the garage,
   * Partners, minus whichever one this is.
   *
   * The panel is the whole of the navigation on a phone: the header's nav moves into it
   * below 992px and the venture links sit in the footer, which on a phone is several
   * screens of scrolling below the fold. So the one place a phone visitor can leave for
   * another KKF site was a footer they had to reach by scrolling past the page.
   *
   * **An app never lists itself.** It is composed by the app, not derived here, because
   * the design system has no idea which of the four it is being rendered inside — and a
   * link back to the site you are already on is the one entry a menu must not carry.
   *
   * Every entry is an `href`: these are other origins, so they are plain `<a>`s and never
   * router links. Omitted, the block is not rendered at all.
   */
  sites?: { label: string; href: string }[]
  /** The heading over `sites`. Required when there are any — see ground rule 8. */
  sitesTitle?: string
  /**
   * A line of scripture at the foot of the panel, above the socials.
   *
   * **Composed per app and rendered by exactly one of them today** — Partners, where
   * "As iron sharpens iron" is what the board is for: businesses who sharpen each other.
   * The design system does not know which app it is inside and has no business choosing a
   * verse for one, so this is a slot and not a default. Omitted, nothing is rendered.
   *
   * `text` and `reference` arrive resolved — both are editorial `Text` in the app's data
   * and both are written in each language, the way `NotFound`'s verse already is. The
   * reference is translated as well as the words, because Mongolian transliterates the book
   * names.
   */
  verse?: { text: string; reference: string }
  /** The nav node, passed in only below 992px — see `AppShell`. */
  nav?: ReactNode
}

/**
 * The off-canvas panel. Byte-identical across all 19 source pages, so it takes no
 * page-level configuration — only the shell content and the nav slot.
 *
 * `.ul-sidebar.active` is what slides it in (`transform: translateX(0)`).
 *
 * ## What changed on 2026-09-19
 *
 * It was the template's drawer and it behaved like one: no scrim, no Escape, and nothing
 * stopping a keyboard from tabbing into it while it sat off the right edge of the screen.
 * Three fixes, and the third is the one nobody sees:
 *
 * 1. **A scrim.** Tapping beside a drawer to dismiss it is the gesture people already
 *    have; without one the only way out is a 20px × in the corner. It is also what tells
 *    a reader the page behind is still there and is not what they are looking at.
 * 2. **Escape closes it.** The listener is bound only while the panel is open, so four
 *    apps do not each carry a permanent keydown handler on `document` for a drawer that
 *    is shut.
 * 3. **`inert` while shut.** The panel is always in the DOM — it is hidden by
 *    `translateX(100%)`, which is what lets it animate — so without this, Tab from the
 *    header walks into a menu that is off-screen, and a screen reader reads a nav the
 *    reader cannot see. `display: none` would fix both and cost the transition.
 *
 * Closing on navigation stays `AppShell`'s job: it is the one that knows the route
 * changed, and it also has to close the search overlay on the same signal.
 */
export function MobileSidebar({
  open,
  onClose,
  logo,
  homeTo,
  followTitle,
  socials,
  sites = [],
  sitesTitle,
  verse,
  nav = null,
}: MobileSidebarProps) {
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  return (
    <>
      {/*
        The scrim. Rendered always rather than conditionally, so it can fade rather than
        appear — the panel takes 0.4s to arrive and a ground that blinks in ahead of it
        reads as two separate events.

        `aria-hidden` and no tab stop: Escape and the × are the accessible ways out, and a
        focusable div in front of the page would be a second one that announces nothing.
      */}
      <div
        className={`ul-sidebar-scrim${open ? ' active' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <div className={`ul-sidebar${open ? ' active' : ''}`} inert={!open}>
        {/* header */}
        <div className="ul-sidebar-header">
          <div className="ul-sidebar-header-logo">
            <Link to={homeTo}>
              <img src={logo} alt="logo" className="logo" />
            </Link>
          </div>
          {/* sidebar closer */}
          <button className="ul-sidebar-closer" onClick={onClose} aria-label="Close menu">
            <i className="flaticon-close" />
          </button>
        </div>

        <div className="ul-sidebar-header-nav-wrapper d-block d-lg-none">{nav}</div>

        {/* sidebar footer */}
        <div className="ul-sidebar-footer">
          {/*
            The other KKF sites, above the socials.

            Above rather than below because leaving for another venture is navigation and
            following us is not, and the panel's last block is the one a thumb reaches
            first — but a reader scanning a menu reads it as the end of the list. The
            socials keep that position.
          */}
          {sites.length > 0 && (
            <div className="ul-sidebar-sites">
              <span className="ul-sidebar-footer-title">{sitesTitle}</span>
              <div className="ul-sidebar-sites-links">
                {sites.map((site) => (
                  <a key={site.href} href={site.href}>
                    {site.label}
                    <i className="flaticon-fast-forward-double-right-arrows-symbol" aria-hidden="true" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/*
            The verse, between the way out and the way to follow us.

            Below the sites because it is not navigation and above the socials because the
            socials are the panel's last block — a reader scanning a menu reads that
            position as the end of the list, and a line of scripture is not an end.
          */}
          {verse && (
            <figure className="ul-sidebar-verse">
              <blockquote>{verse.text}</blockquote>
              <figcaption>{verse.reference}</figcaption>
            </figure>
          )}

          <span className="ul-sidebar-footer-title">{followTitle}</span>

          <div className="ul-sidebar-footer-social">
            {socials.map((s) => (
              <a key={s.icon} href={s.href} aria-label={s.label ?? s.icon}>
                <i className={`flaticon-${s.icon}`} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
